import ActionButtonRounded from 'Components/ActionButtonRounded';
import DataTable from 'Components/DataTable';
import { Container, Item } from 'Components/Grid';
import HistoryRowSubHeader from 'Components/HistoryRowSubHeader';
import { exists, formatTime, isPartialDate, isTime, sortPartialDate } from 'neuro-utils';
import { formatPartialDate } from 'Utility/date';
import * as React from 'react';
import { useMyServiceContext } from '..';
import NewTreatmentDecisionCell from './components/NewTreatmentDecisionCell';
import { useAppDispatch as useDispatch, useAppSelector as useSelector } from 'Store/index';
import { actions } from 'Store/myapp/actions';
import ConfirmationDialog from 'Components/ConfirmationDialog';
import colors from '../../../../config/theme/colors';
import { styled } from '@mui/system';
import { msSurveyTypes } from '../config';
import { Clear, Replay, SupervisorAccount } from '@mui/icons-material';
import RecurringTasksList from './components/RecurringTasksList';
import { getTaskActionTypeOptions } from 'Routes/MyService/util';
import InputHandler from 'Components/InputHandler';
import ToolTip from 'Components/ToolTip';
import Icon from 'Components/_NewElements/Icon';
import browserLanguage from 'Utility/browserLanguage';
import { DateTime } from 'luxon';
import {
  canEditTasklist,
  // convertTaskRecurrenceToTTaskRecurrence,
  partialDateFromEndExclusive,
  partialDateFromStartInclusive,
  TASK_DATE_FORMAT,
} from 'Utility/tasklist';
// import { getRecurringTaskInstances } from 'neuro-common-utils';

const appointmentKeys: Array<keyof IAppointment> = [
  'startDate',
  'startTime',
  'status',
  'externalAppointmentType',
  'locationGroupId',
  'locationGroupName',
  'locationTypeId',
  'locationTypeText',
  'locationId',
  'locationName',
  'endDate',
  'endTime',
  'comments',
];

const AppointmentData = ({ appointment }: IAppointmentData) => {
  const { fm } = useMyServiceContext();
  return (
    <div>
      {appointmentKeys.map(key => {
        const localeKey =
          key.substring(0, 8) === 'location' ? key.replace(/id|name|text/i, match => '.' + match.toLowerCase()) : key;

        const rawValue = appointment[key];
        const value: string | null | undefined = exists(rawValue)
          ? isPartialDate(rawValue)
            ? formatPartialDate(rawValue)
            : isTime(rawValue)
              ? formatTime(rawValue)
              : key === 'status'
                ? fm('appointments.opts.' + rawValue)
                : `${rawValue}`
          : undefined;
        if (!value) return null;
        return (
          <Container key={key}>
            <Item xs="auto">{fm('appointments.' + localeKey)}</Item>
            <Item xs={true} style={{ textAlign: 'right', fontWeight: 600, paddingLeft: '1rem' }}>
              {value}
            </Item>
          </Container>
        );
      })}
    </div>
  );
};

interface IAppointmentData {
  appointment: IAppointment;
}

const ClearIconStyled = styled(Clear)({
  display: 'block',
  width: '3rem',
  color: colors.secondaryText,
  cursor: 'pointer',
  alignSelf: 'center',
  '&:hover': {
    color: 'red',
  },
});

const TaskListHistory = ({ treatmentDecisionDocs, taskLists }: IOwnProps): React.JSX.Element => {
  const { fm, setEditingObj } = useMyServiceContext<'_tasklist'>();
  const [showPast, setShowPast] = React.useState<boolean>(false);
  const [loading, setLoading] = React.useState<boolean>(false);

  const locale = useSelector(s => s.settings.userSettings.uiLanguage) ?? browserLanguage;
  const platform = useSelector(s => s.session.platforms?.selected);
  // const availablePlatforms = useSelector(s => s.session.platforms?.available) ?? [];
  const patientContactInfo = useSelector(s => s.patient.contactInfo);
  const taskOrgSettings = useSelector(s => s.settings.orgSettings.taskUiSettings);
  const appointments = useSelector(s => s.appointments.appointments) ?? [];

  const templateSettings: TaskStimulusFormSettings = platform ? (taskOrgSettings?.[platform] ?? {}) : {};
  const availableTemplateIDs = new Set<string>([...Object.keys(templateSettings), 'other']);

  // const classifiedTasklists = classifyTasklists({ tasklists: taskLists, availablePlatforms });

  const historyData: Array<IDocumentWithTypeV2<'ninmtTreatmentPeriod'> | ITaskList> = [
    ...(platform === 'ninmt' ? treatmentDecisionDocs : []),
    ...taskLists.filter(tl => {
      const taskActionTypeOptions = getTaskActionTypeOptions(platform, !!tl.informant_id || tl.task_kind === 'IROM');
      // TODO this filtering might be problematic
      if (!tl.tasks?.every(t => taskActionTypeOptions.includes(t))) return false;
      if (!availableTemplateIDs.has(tl.template_id)) return false;
      const end = tl.end_exclusive ? DateTime.fromFormat(tl.end_exclusive, TASK_DATE_FORMAT) : undefined;
      if (!showPast && end && end < DateTime.now()) return false;
      return true;
    }),
  ].sort((a, b) => {
    const b_date =
      'end_exclusive' in b && b.end_exclusive
        ? partialDateFromEndExclusive(b.end_exclusive)
        : 'date' in b
          ? b.date
          : undefined;

    const a_date =
      'end_exclusive' in a && a.end_exclusive
        ? partialDateFromEndExclusive(a.end_exclusive)
        : 'date' in a
          ? a.date
          : undefined;

    return sortPartialDate(b_date, a_date);
  });

  const matchAppointmentToId = (id: string | null | undefined) =>
    id ? appointments?.find(a => a.id === id) : undefined;

  const dispatch = useDispatch();

  const [deleteId, setDeleteId] = React.useState<string | null>(null);

  const historyDataToTable: NonNullable<Parameters<typeof DataTable>[0]['data']>['rowData'] = historyData
    .map(data => {
      if ('date' in data && data.date && platform === 'ninmt') {
        return <NewTreatmentDecisionCell fm={fm} date={data.date} key={data._id} />;
      } else if ('task_kind' in data) {
        const { template_id, tasks, appointment_id /*, timezone, start_inclusive, end_exclusive, recurrence */ } = data;
        const sortedTasks =
          platform === 'ms'
            ? tasks?.toSorted(
                // oxlint-disable-next-line unicorn/prefer-array-index-of
                (a, b) => msSurveyTypes.findIndex(type => type === a) - msSurveyTypes.findIndex(sType => sType === b),
              )
            : tasks;

        const tasksFormatted = sortedTasks
          ?.map(t => {
            if (t === 'memory' && data.informant_id) return fm('myService.parkinson.memoryInformant');
            return fm(`myService.${platform}.opts.${t}`);
          })
          .join(', ');

        /** format title */
        const title: string | undefined =
          template_id && templateSettings[template_id]?.titleLocalized
            ? (templateSettings[template_id]?.titleLocalized[locale] ??
              templateSettings[template_id]?.titleLocalized['fi'])
            : template_id === 'other'
              ? fm('myService.opts.other')
              : template_id;

        const appointment = matchAppointmentToId(appointment_id);

        // const ttaskRecurrence = convertTaskRecurrenceToTTaskRecurrence({ recurrence }); // TODO

        // const instances = ttaskRecurrence
        //   ? getRecurringTaskInstances({
        //       task: {
        //         recurrence: ttaskRecurrence,
        //         timezone,
        //         start_inclusive,
        //         end_exclusive,
        //       },
        //     })
        //   : null;

        return [
          <div key="deadline" style={{ whiteSpace: 'nowrap', display: 'flex', paddingRight: '0.5rem' }}>
            <div>
              {!data.recurrence.recurring && data.end_exclusive
                ? formatPartialDate(partialDateFromEndExclusive(data.end_exclusive))
                : null}
            </div>
            {/* <div>
              {data.recurrence.recurring &&
              data.recurrence.every_nth &&
              data.recurringTaskDeadline &&
              partialDateToValue(data.deadline) >
                partialDateToValue(getNextRecurringDeadline(data.recurringTaskDeadline, data.weeklySurveyInterval))
                ? formatPartialDate(getNextRecurringDeadline(data.recurringTaskDeadline, data.weeklySurveyInterval))
                : formatPartialDate(data.deadline)}
            </div> */}
            {appointment && (
              <ToolTip
                content={<Icon icon="bell" button />}
                hover
                title={fm('appointments.appointment')}
                description={<AppointmentData appointment={appointment} />}
              />
            )}
          </div>,
          title ?? '-',
          tasksFormatted ?? '-',
          data.recurrence.recurring || data.informant_id ? (
            <>
              {data.recurrence.recurring && (
                <ToolTip
                  title={fm('myService.recurring')}
                  hover
                  content={
                    <div style={{ display: 'flex' }}>
                      <Replay
                        fontSize="small"
                        sx={{
                          color:
                            data.end_exclusive &&
                            DateTime.fromFormat(data.end_exclusive, TASK_DATE_FORMAT).setZone(data.timezone) >
                              DateTime.now()
                              ? colors.success
                              : undefined,
                        }}
                      />
                    </div>
                  }
                />
              )}
              {data.informant_id && (
                <ToolTip
                  hover
                  title={fm('myService.forRelative')}
                  content={
                    <div style={{ display: 'flex' }}>{data.informant_id && <SupervisorAccount fontSize="small" />}</div>
                  }
                />
              )}
            </>
          ) : (
            ''
          ),
          <div key="start_inclusive" style={{ whiteSpace: 'nowrap' }}>
            {data.start_inclusive ? formatPartialDate(partialDateFromStartInclusive(data.start_inclusive)) : null}
          </div>,
          <div key={data.id} style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <ActionButtonRounded
              text="general.edit"
              filled={true}
              width={9.6}
              height={2.5}
              fontSize={16}
              uppercase={false}
              onClick={() => setEditingObj({ type: '_tasklist', data })}
              disabled={!canEditTasklist({ tasklist: data })}
            />
            <ClearIconStyled onClick={() => setDeleteId(data.id)} />
          </div>,
        ];
      } else return 'filter';
    })
    .filter(d => d !== 'filter');

  const activeAndUpcomingRecurringTasks = historyData.filter(
    d =>
      'recurrence' in d &&
      d.recurrence.recurring &&
      (!d.end_exclusive || DateTime.fromFormat(d.end_exclusive, TASK_DATE_FORMAT) >= DateTime.now()),
  ) as Array<ITaskList>;

  const hasTaskUISettingsForPlatform = !!(platform && taskOrgSettings?.[platform]);

  return (
    <>
      <Container style={{ justifyContent: 'space-between' }}>
        <Item>
          <HistoryRowSubHeader header={fm('myService.taskLists')} />
        </Item>
        <Item>
          <ActionButtonRounded
            text="myService.newTaskList"
            filled={true}
            uppercase={false}
            width={9.6}
            height={3}
            fontSize={16}
            onClick={() => setEditingObj({ type: '_tasklist', data: {} })}
            disabled={!hasTaskUISettingsForPlatform || !!patientContactInfo?.contactNotAllowed}
            disabledTooltip={
              <span>
                {!hasTaskUISettingsForPlatform
                  ? fm('myService.noTasklistSettingsDefined')
                  : fm('myService.newTaskListDisabledTooltip')}
              </span>
            }
          />
        </Item>
      </Container>
      {platform && activeAndUpcomingRecurringTasks.length > 0 ? (
        <>
          <Container>
            <Item xs={9} md={7}>
              <RecurringTasksList platform={platform} recurringTasks={activeAndUpcomingRecurringTasks} showEndButton />
            </Item>
          </Container>
          <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
            <HistoryRowSubHeader header={fm('myService.all')} fontSize="small" />
            <div style={{ marginRight: '-0.5rem' }}>
              <InputHandler
                type="CheckboxSingle"
                name="showPast"
                editing={true}
                value={showPast ? 'showPast' : ''}
                option={'showPast'}
                showLabel
                formData={{
                  onChange: () => setShowPast(!showPast),
                  document: { showPast },
                }}
                optionFormatter={() => fm('myService.showPastTasks')}
                labelFirst
              />
            </div>
          </div>
        </>
      ) : null}
      {activeAndUpcomingRecurringTasks.length === 0 ? (
        <div style={{ display: 'flex', marginRight: '-0.5rem', justifyContent: 'end' }}>
          <InputHandler
            type="CheckboxSingle"
            name="showPast"
            editing={true}
            value={showPast ? 'showPast' : ''}
            option={'showPast'}
            showLabel
            formData={{
              onChange: () => setShowPast(prev => !prev),
              document: { showPast },
            }}
            optionFormatter={() => fm('myService.showPastTasks')}
            labelFirst
          />
        </div>
      ) : null}
      <DataTable
        headers={['deadline', 'taskListTitle', 'surveysAndDiaries', '', 'sendDate', 'hidden']}
        headersFormatter={h => (h === 'hidden' || h === '' ? '' : fm(`myService.${h}`))}
        data={{ rowData: historyDataToTable }}
      />
      <ConfirmationDialog
        open={!!deleteId}
        text={fm('general.reallyWantToDelete')}
        cancel={{ callback: () => setDeleteId(null) }}
        confirm={{
          callback: async () => {
            setLoading(true);
            const tasklistToBeDeleted = taskLists.find(d => d.id === deleteId);
            if (tasklistToBeDeleted) await actions.deleteTasklist(tasklistToBeDeleted, dispatch);
            setDeleteId(null);
            setLoading(false);
          },
          loading,
        }}
      />
    </>
  );
};

TaskListHistory.displayName = 'TaskListHistory';

interface IOwnProps {
  treatmentDecisionDocs: IDocumentWithTypeV2<'ninmtTreatmentPeriod'>[];
  taskLists: Array<ITaskList>;
}

export default TaskListHistory;
